import { scrollToWhenReady } from "./evaluationRules";

/**
 * Jumps inside the session report (평가 기준 chip → criterion card, 말풍선 → transcript bubble,
 * …) with a way back. Every jump goes through jumpTo(), which first records where the reader
 * was — phone tab, page scroll, the scroll of each desktop column (data-report-scroll), and
 * the card they jumped from — then pushes a browser history entry. Going back, by the
 * 돌아가기 pill or the phone's own back gesture, pops that entry and restores it.
 *
 * Module state, not React state: the call sites are deep in cards that don't share a parent
 * hook, and the report is the only page that jumps. installJumpBack() scopes it to the
 * report's lifetime.
 */

export const JUMP_STACK_EVENT = "report:jump-stack"; // the stack changed (the pill re-renders)
export const RESTORE_EVENT = "report:restore"; // detail: the entry to restore

const stack = [];
// What can be flashed on the way back: report blocks only (a bare "[id]" climbs to #root).
const ORIGINS = '[id^="criterion_int_id_"], [id^="claim_"], [id^="summary_section_"], #summary_lists';
let currentTab = null;

/** The phone report's active tab, recorded with each jump (ChatSessionReport keeps it current). */
export const setReportTab = (tab) => {
  currentTab = tab;
};

export const jumpDepth = () => stack.length;

const isVisible = (el) => !!el && el.getClientRects().length > 0;

const announce = () => window.dispatchEvent(new CustomEvent(JUMP_STACK_EVENT));

/**
 * Go to `targetId`. If it isn't on screen (another phone tab, or filtered out), `showEvent`
 * with `detail` asks its owner to show it. `origin` is the element tapped: the report block
 * it sits in (ORIGINS: a criterion or claim card, a summary section) flashes on the way back.
 */
export const jumpTo = (targetId, { showEvent, detail, origin } = {}) => {
  stack.push({
    tab: currentTab,
    y: window.scrollY,
    panes: [...document.querySelectorAll("[data-report-scroll]")].map((el) => [el.dataset.reportScroll, el.scrollTop]),
    originId: origin?.closest?.(ORIGINS)?.id || null,
  });
  // Keep React Router's own fields (key, idx) so its history bookkeeping stays intact.
  window.history.pushState({ ...(window.history.state || {}), reportJump: stack.length }, "");
  announce();

  if (isVisible(document.getElementById(targetId))) scrollToWhenReady(targetId);
  else if (showEvent) window.dispatchEvent(new CustomEvent(showEvent, { detail }));
};

/** Undo the last jump: the 돌아가기 pill goes through history, so the back gesture and it agree. */
export const jumpBack = () => {
  if (stack.length) window.history.back();
};

const onPopState = (e) => {
  const depth = e.state?.reportJump || 0;
  if (stack.length <= depth) return;
  let entry;
  while (stack.length > depth) entry = stack.pop();
  window.dispatchEvent(new CustomEvent(RESTORE_EVENT, { detail: entry }));
  announce();
};

/** Put scroll positions back and flash the card the reader jumped from. Called after the tab renders. */
export const restoreScroll = (entry) => {
  window.scrollTo(0, entry.y);
  entry.panes.forEach(([name, top]) => {
    const el = document.querySelector(`[data-report-scroll="${name}"]`);
    if (el) el.scrollTop = top;
  });
  const origin = entry.originId && document.getElementById(entry.originId);
  if (origin) {
    origin.classList.add("highlight");
    setTimeout(() => origin.classList.remove("highlight"), 1000);
  }
};

/** Mount with the report; the returned cleanup forgets any jumps when it unmounts. */
export const installJumpBack = () => {
  window.addEventListener("popstate", onPopState);
  return () => {
    window.removeEventListener("popstate", onPopState);
    stack.length = 0;
    announce();
  };
};
